import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Radio,
	Typography,
	useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { useGetAdmissionRequestAllQuery } from '@/api/member-management/admissionRequest';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import { Link, useNavigate } from 'react-router-dom';
import MemberStatusChip from '@/common/components/MemberStatusChip';

interface Props {}

const NewFamilyMemberButton = (props: Props) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const [open, setOpen] = useState(false);
	const showModal = useModal();
	const [selectedRowId, setSelectedRowId] = useState<number | string | null>(
		null
	);
	const navigate = useNavigate();

	const defaultValues: DefaultValues<any> = {
		pagination: { ...initialPagination, size: 5 },
	};
	const methods = useForm<any>({
		defaultValues,
	});
	const watchSearchString = methods.watch('searchString');
	const watchPagination = methods.watch('pagination');

	// Reset to first page whenever the search string changes.
	useEffect(() => {
		methods.setValue('pagination', {
			...methods.getValues('pagination'),
			page: 1,
		});
	}, [watchSearchString]);

	// CHA-005 §2.2: admitted members of this centre not yet linked to a family member.
	const { data: memberList, isFetching: isMemberListFetching } =
		useGetAdmissionRequestAllQuery(
			{
				statusList: [1, 3, 5, 7],
				sortings: [],
				pagination: watchPagination,
				searchString: watchSearchString,
				excludeLinkedMember: true,
			},
			{
				refetchOnMountOrArgChange: true,
				skip: !open,
			}
		);

	const handleClose = () => {
		setOpen(false);
	};

	const onSubmit = async (data: any) => {
		// const parts = nextFileNo?.data.split('/');
		// const response = await registerCase({
		// 	...data,
		// 	admissionDate: data.admissionDate,
		// 	dateOfCaseOpen: data.dateOfCaseOpen,
		// 	id: admissionRequestId,
		// 	fileNo: `${parts[0]}/${parts[1]}/${data.fileNoB}/${data.fileNoC}`,
		// });
		// if ('data' in response) {
		// 	enqueueSnackbar(t('snackbar.updateMemberSuccess'), {
		// 		variant: 'success',
		// 		anchorOrigin: { horizontal: 'center', vertical: 'top' },
		// 		autoHideDuration: 3000,
		// 		action: (key) => {
		// 			return <CloseIcon onClick={() => closeSnackbar(key)} />;
		// 		},
		// 	});
		// 	handleClose();
		// }
		if (!selectedRowId) return;
		navigate(
			`/family-management/family-management-list/create/${selectedRowId}`
		);
	};

	const handleDialog = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('button.newFamilyMember')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('family.selectTips')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('family.toCreate')}
				otherButtonText={t('family.toSelect')}
				confirmationButtonProps={{ variant: 'contained' }}
				otherButtonProps={{ variant: 'outlined' }}
				onConfirm={async () => {
					navigate(`/family-management/family-management-list/create`);
				}}
				onOtherConfirm={async () => {
					setOpen(true);
				}}
			/>
		));
	};

	const columns: GridColDef[] = [
		{
			field: 'select',
			headerName: '',
			width: 60,
			renderCell: (params) => (
				<Radio
					checked={params.row.id === selectedRowId}
					onChange={() => setSelectedRowId(params.row.id)}
				/>
			),
		},
		{
			field: 'fileNo',
			headerName: t('member.fileNo'),
			flex: 18,
			minWidth: 160,
			renderCell: (params) => (
				<Link
					to={`/member-management/member-list/detail/${params.row.id}`}
					target='_blank'
					rel='noopener noreferrer'
					onClick={(e) => e.stopPropagation()}
					style={{ color: theme.palette.primary.main }}
				>
					{params.row.fileNo}
				</Link>
			),
		},
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 22,
			minWidth: 160,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{
			field: 'phone',
			headerName: t('member.mobilePhone'),
			flex: 14,
			minWidth: 120,
		},
		{
			field: 'status',
			headerName: t('member.status'),
			flex: 24,
			minWidth: 240,
			renderCell: (params) => (
				<MemberStatusChip
					status={params.row.status}
					label={params.row.statusDesc}
				/>
			),
		},
	];

	return (
		<>
			<Button
				variant='contained'
				endIcon={<AddCircleOutlineOutlinedIcon />}
				onClick={() => {
					handleDialog();
				}}
				style={{ alignItems: 'center' }}
			>
				{t('button.newFamilyMember')}
			</Button>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<DialogTitle variant='h5'>{t('family.linkMember')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('family.linkMemberText')}
					</Typography>
					<FormProvider {...methods}>
						<Grid
							container
							spacing={2}
							marginTop={0}
							marginBottom={2}
							alignItems='center'
						>
							<Grid size={12}>
								<AppSummarySearchBar
									name='searchString'
									control={methods.control}
									placeholder={t('common.search')}
								/>
							</Grid>
							<Grid size={12}>
								<AppDataGrid
									loading={isMemberListFetching}
									rows={memberList?.data.items || []}
									rowCount={memberList?.data.pagination.totalItems ?? 0}
									columns={columns}
									getRowId={(row) => row.id}
									control={methods.control}
								/>
							</Grid>
						</Grid>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						disabled={!selectedRowId}
						onClick={onSubmit}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default NewFamilyMemberButton;
